/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';

import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import Task from '../../../domain/models/Task';
import getTasks from '../../../domain/use_cases/tasks/getTasks';
import { ETaskStatus } from '@leedpulse-monorepo/enums';

export const TASKS_FETCH_LIMIT = 10; // NOTE : It can be 25

const TasksContext = createContext({
  toDoCount: 0,
  completedCount: 0,
  hitsPerPage: TASKS_FETCH_LIMIT,
  itemsToDo: [] as Task[],
  itemsCompleted: [] as Task[],
  lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
  scopedContactId: null as string | null,
  fetch: (
    status: ETaskStatus,
    contactIdToScopeOn: string | null,
    limit?: number
  ) => {},
  fetchNext: (
    status: ETaskStatus,
    contactIdToScopeOn: string | null
  ) => {},
  reset: () => {},
  status: ContextStatus.IDLE
});

export const TasksProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [toDoCount, setToDoCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [hitsPerPage, setHitsPerPage] = useState(TASKS_FETCH_LIMIT);
  const [itemsToDo, setItemsToDo] = useState([] as Task[]);
  const [itemsCompleted, setItemsCompleted] = useState([] as Task[]);
  const [lastVisible, setLastVisible] = useState(
    {} as QueryDocumentSnapshot<DocumentData, DocumentData>
  );
  const [scopedContactId, setScopedContactId] = useState(null as string | null);
  const [status, setStatus] = useState(ContextStatus.IDLE);

  const fetch = async (
    status: ETaskStatus,
    contactIdToScopeOn: string | null,
    limit?: number
  ) => {
    const effectiveLimit = limit ?? hitsPerPage;
    if (limit !== undefined) setHitsPerPage(limit);
    setScopedContactId(contactIdToScopeOn);
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getTasks(
        effectiveLimit,
        undefined,
        contactIdToScopeOn,
        status
      );
      if (status === ETaskStatus.Completed) {
        setItemsCompleted(useCaseResponse.tasks);
        setCompletedCount(useCaseResponse.totalCollectionCount);
      } else {
        setItemsToDo(useCaseResponse.tasks);
        setToDoCount(useCaseResponse.totalCollectionCount);
      }
      setLastVisible(useCaseResponse.lastVisible);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchNext = async (
    status: ETaskStatus,
  ) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getTasks(
        hitsPerPage,
        lastVisible,
        scopedContactId,
        status
      );
      if (status === ETaskStatus.Completed) {
        setItemsCompleted([...itemsCompleted, ...useCaseResponse.tasks]);
      } else {
        setItemsToDo([...itemsToDo, ...useCaseResponse.tasks])
      }
      setLastVisible(useCaseResponse.lastVisible);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const reset = () => {
    setCompletedCount(0);
    setToDoCount(0);
    setHitsPerPage(TASKS_FETCH_LIMIT);
    setItemsCompleted([]);
    setItemsToDo([]);
    setLastVisible({} as QueryDocumentSnapshot<DocumentData, DocumentData>);
    setStatus(ContextStatus.IDLE);
  };

  return (
    <TasksContext.Provider
      value={{
        completedCount,
        toDoCount,
        hitsPerPage,
        itemsCompleted,
        itemsToDo,
        lastVisible,
        scopedContactId,
        fetch,
        fetchNext,
        reset,
        status
      }}
    >
      {children}
    </TasksContext.Provider>
  );
};

export default TasksContext;
